import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Briefcase, Plus, Search } from "lucide-react";
import DropDownWithSearch from "../../components/shared/DropDownWIthSearch";
import RecentSearchesPanel, {
    SearchCountChips,
    SearchRowPlaceholder,
    SearchRowSkeleton,
    SEARCH_ROW_CLASS,
    timeAgo,
} from "./RecentSearchesPanel";

// How many searches sit under the search bar. The slot is always this tall, filled or
// not, so the heading above it never moves.
const RECENT_SLOTS = [0, 1, 2];

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const Sourcing = ({ userName = "there" }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const portal = location.pathname.startsWith("/hr") ? "hr" : "org-admin";
    const orgAdminId = localStorage.getItem("org_admin_id");

    const [query, setQuery] = useState("");
    const [jobs, setJobs] = useState([]);
    // Preselect the job when arriving from a job-role detail or Find Candidates ("New search").
    const incomingJobRole = location.state?.job_role || "";
    const [jobId, setJobId] = useState(location.state?.job_id || "");
    const [loadingJobs, setLoadingJobs] = useState(true);

    const [searches, setSearches] = useState([]);
    const [loadingSearches, setLoadingSearches] = useState(false);
    const [allSearchesOpen, setAllSearchesOpen] = useState(false);

    // Every search must be tied to a GenieHire job role — load the recruiter's jobs.
    useEffect(() => {
        if (!orgAdminId) { setLoadingJobs(false); return; }
        axios
            .get(`${SOURCING_URL}/sourcing/jobs`, { params: { created_by: orgAdminId } })
            .then(({ data }) => setJobs(data.jobs || []))
            .catch(() => setJobs([]))
            .finally(() => setLoadingJobs(false));
    }, [orgAdminId]);

    // Recent searches for the selected job role — refetched whenever the picker changes,
    // and nothing to show until a role is picked. One fetch feeds both the three rows
    // under the search bar and the "view all" panel.
    useEffect(() => {
        if (!jobId) { setSearches([]); setLoadingSearches(false); return; }
        // Switching roles quickly can land responses out of order; ignore a stale one.
        let cancelled = false;
        setLoadingSearches(true);
        axios
            .get(`${SOURCING_URL}/sourcing/searches`, { params: { limit: 50, all: true, job_id: jobId } })
            .then(({ data }) => { if (!cancelled) setSearches(data.searches || []); })
            .catch(() => { if (!cancelled) setSearches([]); })
            .finally(() => { if (!cancelled) setLoadingSearches(false); });
        return () => { cancelled = true; };
    }, [jobId]);

    const recentSearches = useMemo(() => searches.slice(0, RECENT_SLOTS.length), [searches]);
    // Nothing to head when the slot is only holding space.
    const showRecentLabel = !!jobId && (loadingSearches || recentSearches.length > 0);

    // Opens Find Candidates scoped to that one search, rather than replaying the search:
    // the pool is already persisted, and that screen is where the candidates are worked.
    const openSearch = (s) => {
        setAllSearchesOpen(false);
        const targetJob = s.job_id || jobId;
        if (!targetJob) return;
        navigate(`/${portal}/find-candidates/${targetJob}`, {
            state: {
                job_id: targetJob,
                job_role: s.job_role || selectedJob?.jobrole || incomingJobRole,
                search_id: s.search_id,
            },
        });
    };

    const selectedJob = jobs.find(j => j.job_id === jobId) || null;
    const canSearch = query.trim() && jobId;

    // Shape jobs into the { label, value } options the shared dropdown expects.
    const jobOptions = useMemo(
        () => jobs.map(j => ({ label: j.jobrole, value: j.job_id })),
        [jobs]
    );

    // Fall back to the role name we were navigated with so the preselection shows straight
    // away, rather than reading as an empty picker while /sourcing/jobs is still loading.
    const selectedOption = useMemo(
        () => (jobId ? { label: selectedJob?.jobrole || incomingJobRole, value: jobId } : null),
        [jobId, selectedJob, incomingJobRole]
    );

    const handleSubmit = () => {
        if (!canSearch) return;
        navigate(`/${portal}/sourcing/preview`, {
            state: { query, job_id: jobId, job_role: selectedJob?.jobrole || location.state?.job_role || null },
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
        }
    };

    const noJobs = !loadingJobs && jobs.length === 0;

    return (
        <div className="relative h-full bg-bgPrimary dark:bg-none px-6 overflow-y-auto">

            {/* min-h-full + justify-center keeps the bar centred on tall screens, but lets the
                recent-searches list below it scroll rather than clip on short ones. */}
            <div className="flex flex-col items-center justify-center min-h-full py-10">
                <h1 className="text-3xl font-normal text-textPrimary mt-32 mb-10 text-center">
                    Hey {userName}, who are you looking for?
                </h1>

                <div className="w-full max-w-4xl flex flex-col gap-3">
                    {/* Single horizontal search bar — query + job-role picker inline */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center flex-1 min-w-0 gap-2 pl-5 pr-2 py-2.5 rounded-full border border-gray-200 dark:border-gray-600 bg-bgPrimary shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                            <Search className="w-5 h-5 text-gray-400 dark:text-gray-500 shrink-0" />
                            <input
                                type="text"
                                name="sourcing-query"
                                autoComplete="off"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Software Engineers in SF working at Series B companies, skilled in Python and Node.js"
                                className="flex-1 min-w-0 bg-transparent text-textPrimary placeholder-gray-400 text-sm outline-none"
                            />

                            {/* Mandatory job-role picker — inside the search bar */}
                            {noJobs ? (
                                <button
                                    onClick={() => navigate(`/${portal}/jd-selection`)}
                                    className="flex items-center gap-2 shrink-0 pl-3 border-l border-gray-200 dark:border-gray-600 text-sm font-medium text-purple-600 dark:text-purple-300 hover:text-purple-700 transition-all"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create a Job Role
                                </button>
                            ) : (
                                <div className="shrink-0 pl-2 border-l border-gray-200 dark:border-gray-600">
                                    <DropDownWithSearch
                                        dropdownData={jobOptions}
                                        selectedValue={selectedOption}
                                        onSelect={(item) => setJobId(item.value)}
                                        placeholder={loadingJobs ? "Loading job roles…" : "Select Job Role"}
                                        showClearButton={false}
                                        className="w-56"
                                        specialClasses="!py-1.5 !border-purple-200 !rounded-full !text-sm"
                                    />
                                </div>
                            )}
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={!canSearch}
                            title={!jobId ? "Select a Job Role first" : undefined}
                            className="px-8 py-3 rounded-full text-sm font-medium text-white disabled:opacity-50 transition-all shrink-0"
                            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                        >
                            Search
                        </button>
                    </div>

                    {/* Kept in the layout once dismissed, for the same reason as the slot below */}
                    <p className={`text-xs text-gray-400 pl-5 ${!noJobs && !jobId ? "" : "invisible"}`}>
                        Select a Job Role to associate this search with.
                    </p>

                    {/* Recent searches for the selected role. The slot keeps its full height in
                        every state — loading, empty, no role picked — because this sits inside a
                        vertically centred column: anything that grows here would drag the heading
                        up with it. Skeletons cover the fetch so rows fade in rather than pop. */}
                    <div className="mt-1 flex flex-col gap-2">
                        <p className={`text-xs font-medium text-gray-400 pl-1 ${showRecentLabel ? "" : "invisible"}`}>
                            Recent searches
                        </p>

                        {RECENT_SLOTS.map(i => {
                            if (loadingSearches) return <SearchRowSkeleton key={i} />;
                            const s = recentSearches[i];
                            if (!s) return <SearchRowPlaceholder key={i} />;
                            return (
                                <button
                                    key={s.search_id}
                                    onClick={() => openSearch(s)}
                                    className={`${SEARCH_ROW_CLASS} border-gray-200 dark:border-gray-700/50 bg-bgPrimary dark:glass-effect hover:border-purple-200 dark:hover:border-purple-500/40 hover:shadow-sm`}
                                >
                                    <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm text-textPrimary dark:text-[#D9D9D9] truncate">
                                            {s.alias || s.raw_query}
                                        </p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                                            {`${s.result_count ?? 0} candidates · ${timeAgo(s.created_at)}`}
                                        </p>
                                    </div>
                                    <SearchCountChips search={s} />
                                </button>
                            );
                        })}

                        {/* Held open whether or not the button shows — same reason as the rows above.
                            Only worth offering once there's more than the three rows already show. */}
                        <div className="h-9 mt-2 flex justify-center">
                            {!loadingSearches && searches.length > RECENT_SLOTS.length && (
                                <button
                                    onClick={() => setAllSearchesOpen(true)}
                                    className="border-btn dark:border-btn-dark rounded-full text-sm font-medium transition-all"
                                >
                                    View all
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {allSearchesOpen && (
                <RecentSearchesPanel
                    searches={searches}
                    loading={loadingSearches}
                    jobRole={selectedJob?.jobrole || incomingJobRole}
                    onClose={() => setAllSearchesOpen(false)}
                    onOpenSearch={openSearch}
                />
            )}
        </div>
    );
};

export default Sourcing;
